import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { NotebookPen, X, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { noteForPath } from "@/data/schools/speakerNotes";

const OPEN_KEY = "wekit.schools.speakerNotes.open";

/**
 * Speaker Notes panel — a presenter-only, route-aware talking-points
 * drawer. Toggle with `N` (ignored while typing). Hidden in print.
 * State persists across route changes so the notes stay open while
 * the presenter clicks through the flow.
 */
export default function SpeakerNotes() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(OPEN_KEY) === "1";
  });

  useEffect(() => {
    localStorage.setItem(OPEN_KEY, open ? "1" : "0");
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (typing) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const note = noteForPath(pathname);

  return (
    <TooltipProvider delayDuration={150}>
      {/* Toggle button — always visible so operators can find it */}
      {!open && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpen(true)}
              aria-label="Open speaker notes"
              className="fixed bottom-4 right-4 z-40 no-print rounded-full h-10 gap-1.5 shadow-lg bg-background/95 backdrop-blur"
            >
              <NotebookPen className="w-4 h-4" />
              <span className="hidden sm:inline text-xs font-medium">Notes</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="text-xs">
            Speaker notes · <kbd className="px-1 rounded bg-muted">N</kbd>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Slide-in panel */}
      <aside
        className={`fixed top-16 bottom-4 right-4 z-40 no-print w-[min(360px,calc(100vw-2rem))] transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "translate-x-[calc(100%+1rem)] pointer-events-none"
        }`}
        aria-hidden={!open}
        role="complementary"
        aria-label="Speaker notes"
      >
        <div className="h-full rounded-2xl border bg-background/98 backdrop-blur shadow-xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                <NotebookPen className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Speaker notes
                </div>
                <div className="text-xs font-medium truncate">Presenter view</div>
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setOpen(false)}
              aria-label="Close speaker notes"
              className="h-7 w-7"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {note ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold leading-snug">{note.headline}</h3>
                  {note.timing && (
                    <Badge variant="secondary" className="mt-2 rounded-full text-[10px] gap-1">
                      <Clock className="w-3 h-3" />
                      {note.timing}
                    </Badge>
                  )}
                </div>
                <ul className="space-y-2.5">
                  {note.bullets.map((b, i) => (
                    <li key={i} className="flex gap-2 text-xs leading-relaxed">
                      <span
                        aria-hidden
                        className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {note.transition && (
                  <div className="rounded-lg border bg-muted/30 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                      <ArrowRight className="w-3 h-3" /> Transition
                    </div>
                    <div className="text-xs italic leading-relaxed">"{note.transition}"</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                No speaker notes for this screen yet.
              </div>
            )}
          </div>

          <div className="px-4 py-2 border-t bg-muted/20 text-[10px] text-muted-foreground flex items-center justify-between">
            <span>Presenter only · never shown to audience</span>
            <span>
              <kbd className="px-1 rounded bg-background border">N</kbd> toggle
            </span>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}
